import React from 'react';
import { BRAND } from '../../lib/brand';
import { ErrorList, Field, apiErrors, badge, btnPrimary, btnSmall, compactInput, hint, inputStyle, warnBox } from './ui';

// Transit setup for one corpus, on the transit admin API
// (app/routes/transit_admin.py): find the agency's GTFS feed in the Mobility
// Database catalog, check it, publish it, review landmarks (a person confirms
// every coordinate — an LLM never fills them in), and run test trips.

const RT_LABELS = { trip_updates: 'Trip updates', alerts: 'Service alerts', vehicle_positions: 'Vehicle positions' };
const LANDMARK_IDEAS = ['Airport', 'Main hospital', 'University', 'Downtown / transit center', 'Mall', 'Stadium'];
const EMPTY_TRANSIT = {
  enabled: false, feed: { static_url: '', rt: {}, auth: { type: 'none' } },
  walk: { max_mi: 0.5, extended_max_mi: 1.5 }, landmarks: [],
};

const mapLink = (lat, lon) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;

function Summary({ check }) {
  if (!check) return null;
  const s = check.summary;
  return (
    <div style={{ border: `1px solid ${check.ok ? '#a7f3d0' : '#fecaca'}`, background: check.ok ? '#f0fdf4' : '#fef2f2',
                  borderRadius: '8px', padding: '10px 12px', margin: '10px 0', fontSize: '0.84rem' }}>
      <b>{check.ok ? 'Feed checks out' : 'Feed has problems'}</b>
      {s && (
        <div style={{ marginTop: '4px', color: BRAND.gray600 }}>
          {s.agency} · {s.timezone} · {s.routes} routes · {s.stops} stops · {s.trips} trips
          <br />Service {s.service_start} to {s.service_end}
          {typeof s.rt_trip_match === 'number' && <> · live trips matching the schedule: {Math.round(s.rt_trip_match * 100)}%</>}
          {s.hubs && s.hubs.length > 0 && <><br />Busiest stops: {s.hubs.slice(0, 5).join(', ')}</>}
        </div>
      )}
      {(check.errors || []).map((e) => <div key={e} style={{ color: BRAND.danger }}>{e}</div>)}
      {(check.warnings || []).map((w) => <div key={w} style={warnBox}>{w}</div>)}
    </div>
  );
}

export default function TransitPanel({ api, corpusId, transit }) {
  const [draft, setDraft] = React.useState(() => ({ ...EMPTY_TRANSIT, ...(transit || {}),
    feed: { ...EMPTY_TRANSIT.feed, ...((transit && transit.feed) || {}) } }));
  const [query, setQuery] = React.useState('');
  const [feeds, setFeeds] = React.useState(null);
  const [check, setCheck] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [smoke, setSmoke] = React.useState(null);
  const [lmText, setLmText] = React.useState('');
  const [candidate, setCandidate] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState(null);
  const [note, setNote] = React.useState('');

  const call = async (label, fn) => {
    setBusy(label);
    setError(null);
    setNote('');
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };
  const req = async (path, opts) => {
    const { ok, status: code, data } = await api(path, opts);
    if (!ok) {
      const msg = apiErrors(data, `HTTP ${code}`);
      const err = new Error(Array.isArray(msg) ? msg.join('; ') : msg);
      err.status = code;
      throw err;
    }
    return data;
  };

  const loadStatus = React.useCallback(async () => {
    try {
      const { ok, data } = await api(`/api/corpus/transit/status?corpus_id=${encodeURIComponent(corpusId)}`);
      if (ok) setStatus(data);
    } catch { /* status is informational */ }
  }, [api, corpusId]);
  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const setFeed = (patch) => setDraft((d) => ({ ...d, feed: { ...d.feed, ...patch } }));
  const save = (transitBlock) => req('/api/corpus/transit', {
    method: 'PUT', body: JSON.stringify({ corpus_id: corpusId, transit: transitBlock }),
  }).then((data) => { setDraft((d) => ({ ...d, ...data.transit })); return data; });

  const published = status && status.version;
  const auth = draft.feed.auth || { type: 'none' };

  return (
    <div>
      {/* ---- feed ---- */}
      <h3 style={{ fontSize: '0.92rem', margin: '0 0 8px' }}>
        Feed {draft.enabled ? <span style={badge('#d1fae5', '#065f46')}>on</span> : <span style={badge('#f3f4f6', '#4b5563')}>off</span>}
      </h3>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
        <input aria-label="Search the feed catalog" value={query} placeholder="Agency or city, e.g. TARC Louisville"
               style={{ ...inputStyle, marginBottom: 0 }} onChange={(e) => setQuery(e.target.value)} />
        <button type="button" style={btnSmall} disabled={!!busy || query.trim().length < 2}
                onClick={() => call('search', async () => {
                  setFeeds((await req(`/api/transit/catalog?q=${encodeURIComponent(query.trim())}`)).feeds || []);
                })}>
          {busy === 'search' ? 'Searching…' : 'Search catalog'}
        </button>
      </div>
      {feeds && feeds.length === 0 && <p style={hint}>Nothing in the Mobility Database matches. Paste the feed URL from the agency's developer page below.</p>}
      {feeds && feeds.map((f) => (
        <div key={f.mdb_id} style={{ padding: '8px 10px', border: `1px solid ${BRAND.rule}`, borderRadius: '6px', marginBottom: '6px', fontSize: '0.82rem' }}>
          <b>{f.provider}</b>{f.name ? ` — ${f.name}` : ''} <span style={{ color: BRAND.gray500 }}>{f.location}</span>
          <span style={badge(f.status === 'active' ? '#d1fae5' : '#fee2e2', f.status === 'active' ? '#065f46' : '#991b1b')}>{f.status || 'unknown'}</span>
          {f.official && <span style={badge('#e0e7ff', '#3730a3')}>official</span>}
          {f.auth !== 'none' && <span style={badge('#fef3c7', '#92400e')}>needs a key ({f.auth})</span>}
          <div style={{ color: BRAND.gray500 }}>
            {f.mdb_id} · live: {Object.keys(f.rt || {}).map((k) => RT_LABELS[k] || k).join(', ') || 'none'}
            {/^https?:\/\//i.test(f.license_url || '') && <> · <a href={f.license_url} target="_blank" rel="noreferrer">license</a></>}
          </div>
          <button type="button" style={{ ...btnSmall, marginTop: '4px' }} onClick={() => {
            setFeed({ mdb_id: f.mdb_id, static_url: f.static_url || f.mirror_url, rt: f.rt || {},
                      auth: f.auth === 'none' ? { type: 'none' } : { type: f.auth, param: f.auth_param || '', secret_ref: '' } });
            if (!draft.agency_name && f.provider) setDraft((d) => ({ ...d, agency_name: f.provider.slice(0, 60) }));
            setCheck(null);
          }}>
            Use this feed
          </button>
        </div>
      ))}

      <Field id="transit-static-url" label="Schedule feed (GTFS zip)">
        <input id="transit-static-url" value={draft.feed.static_url || ''} style={inputStyle}
               onChange={(e) => setFeed({ static_url: e.target.value.trim() })} />
      </Field>
      {Object.keys(RT_LABELS).map((k) => (
        <Field key={k} id={`transit-rt-${k}`} label={`${RT_LABELS[k]} (GTFS-realtime, optional)`}>
          <input id={`transit-rt-${k}`} value={(draft.feed.rt || {})[k] || ''} style={inputStyle}
                 onChange={(e) => setFeed({ rt: { ...(draft.feed.rt || {}), [k]: e.target.value.trim() } })} />
        </Field>
      ))}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Field id="transit-auth" label="Feed key" style={{ flex: '1 1 160px' }}>
          <select id="transit-auth" value={auth.type} style={inputStyle}
                  onChange={(e) => setFeed({ auth: e.target.value === 'none' ? { type: 'none' } : { ...auth, type: e.target.value } })}>
            <option value="none">None needed</option>
            <option value="query">In the URL query</option>
            <option value="header">In a request header</option>
          </select>
        </Field>
        {auth.type !== 'none' && (
          <>
            <Field id="transit-auth-param" label="Parameter / header name" style={{ flex: '1 1 160px' }}>
              <input id="transit-auth-param" value={auth.param || ''} style={inputStyle}
                     onChange={(e) => setFeed({ auth: { ...auth, param: e.target.value.trim() } })} />
            </Field>
            <Field id="transit-auth-secret" label="Server variable holding the key" style={{ flex: '1 1 220px' }}
                   help="The key itself is never stored here: set this variable (TRANSIT_KEY_…) in the server environment.">
              <input id="transit-auth-secret" value={auth.secret_ref || ''} placeholder="TRANSIT_KEY_AGENCY" style={inputStyle}
                     onChange={(e) => setFeed({ auth: { ...auth, secret_ref: e.target.value.trim().toUpperCase() } })} />
            </Field>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <Field id="transit-agency" label="Agency name (optional)" style={{ flex: '1 1 200px' }}>
          <input id="transit-agency" value={draft.agency_name || ''} style={inputStyle}
                 onChange={(e) => setDraft((d) => ({ ...d, agency_name: e.target.value }))} />
        </Field>
        <Field id="transit-region" label="Region (completes addresses)" style={{ flex: '1 1 200px' }}>
          <input id="transit-region" value={draft.region || ''} placeholder="Louisville, KY" style={inputStyle}
                 onChange={(e) => setDraft((d) => ({ ...d, region: e.target.value }))} />
        </Field>
      </div>

      <Summary check={check} />
      <div style={{ marginBottom: '18px' }}>
        <button type="button" style={btnSmall} disabled={!!busy || !draft.feed.static_url}
                onClick={() => call('check', async () => {
                  setCheck(await req('/api/corpus/transit/check', {
                    method: 'POST', body: JSON.stringify({ corpus_id: corpusId, transit: draft }),
                  }));
                })}>
          {busy === 'check' ? 'Checking the feed…' : 'Check feed'}
        </button>
        <button type="button" style={btnSmall} disabled={!!busy}
                onClick={() => call('save', async () => { await save(draft); setNote('Saved.'); })}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        {!draft.enabled ? (
          <button type="button" style={btnPrimary} disabled={!!busy || !draft.feed.static_url || (check && !check.ok)}
                  onClick={() => call('publish', async () => {
                    await save({ ...draft, enabled: true });
                    const r = await req('/api/corpus/transit/refresh', {
                      method: 'POST', body: JSON.stringify({ corpus_id: corpusId }),
                    });
                    setNote(r.ok ? `Published feed version ${r.version || ''}. Trip planning is on.` : `Saved, but publishing failed: ${r.error}`);
                    await loadStatus();
                  })}>
            {busy === 'publish' ? 'Publishing…' : 'Publish and turn on'}
          </button>
        ) : (
          <button type="button" style={btnSmall} disabled={!!busy}
                  onClick={() => call('off', async () => { await save({ ...draft, enabled: false }); setNote('Trip planning is off.'); })}>
            Turn off
          </button>
        )}
      </div>

      {/* ---- status ---- */}
      {published && (
        <div style={{ fontSize: '0.82rem', color: BRAND.gray600, marginBottom: '18px' }}>
          <b>In service:</b> version {status.feed_version || status.version} · runs {status.service_start} to {status.service_end}
          {typeof status.days_to_expiry === 'number' && (
            <span style={status.days_to_expiry < 14 ? badge('#fee2e2', '#991b1b') : badge('#f3f4f6', '#4b5563')}>
              {status.days_to_expiry} days left
            </span>
          )}
          <br />Last checked {status.last_checked || '—'}{status.last_error ? ` · last error: ${status.last_error}` : ''}
          {(status.warnings || []).map((w) => <div key={w} style={warnBox}>{w}</div>)}
        </div>
      )}

      {/* ---- landmarks ---- */}
      <h3 style={{ fontSize: '0.92rem', margin: '0 0 4px' }}>Landmarks</h3>
      <p style={hint}>
        Places riders name instead of an address. Put the pin where a rider goes (the terminal entrance, not the middle of the
        airfield). Typical: {LANDMARK_IDEAS.join(', ')}.
      </p>
      <div style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
        <input aria-label="Landmark to find" value={lmText} placeholder="Louisville Muhammad Ali International Airport"
               style={{ ...inputStyle, marginBottom: 0 }} onChange={(e) => setLmText(e.target.value)} />
        <button type="button" style={btnSmall} disabled={!!busy || !lmText.trim()}
                onClick={() => call('resolve', async () => {
                  try {
                    const r = await req('/api/corpus/transit/resolve', {
                      method: 'POST', body: JSON.stringify({ corpus_id: corpusId, text: lmText.trim(), kind: 'landmark' }),
                    });
                    if (!r.resolved) {
                      setCandidate({ name: lmText.trim(), lat: '', lon: '', aliases: '', reason: r.reason });
                    } else {
                      setCandidate({ name: lmText.trim(), lat: String(r.place.lat), lon: String(r.place.lon), aliases: '',
                                     label: r.place.label, source: r.source, stops: r.stops || [] });
                    }
                  } catch (e) {
                    if (e.status === 409) throw new Error('Publish the feed first: finding a place uses its stops.');
                    throw e;
                  }
                })}>
          {busy === 'resolve' ? 'Finding…' : 'Find'}
        </button>
      </div>
      {candidate && (
        <div style={{ border: `1px solid ${BRAND.rule}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '10px', fontSize: '0.82rem' }}>
          {candidate.reason
            ? <div style={warnBox}>Not found automatically ({candidate.reason}). Enter the coordinates from a map.</div>
            : <div>Proposed: <b>{candidate.label}</b> ({candidate.source}) · nearby stops: {candidate.stops.slice(0, 4).join(', ') || 'none'}</div>}
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input aria-label="Landmark name" value={candidate.name} style={{ ...compactInput, flex: '2 1 200px' }}
                   onChange={(e) => setCandidate({ ...candidate, name: e.target.value })} />
            <input aria-label="Landmark latitude" value={candidate.lat} placeholder="lat" style={{ ...compactInput, flex: '1 1 90px' }}
                   onChange={(e) => setCandidate({ ...candidate, lat: e.target.value })} />
            <input aria-label="Landmark longitude" value={candidate.lon} placeholder="lon" style={{ ...compactInput, flex: '1 1 90px' }}
                   onChange={(e) => setCandidate({ ...candidate, lon: e.target.value })} />
            <input aria-label="Landmark aliases" value={candidate.aliases} placeholder="aliases, comma-separated"
                   style={{ ...compactInput, flex: '2 1 180px' }} onChange={(e) => setCandidate({ ...candidate, aliases: e.target.value })} />
          </div>
          {candidate.lat && candidate.lon && (
            <a href={mapLink(candidate.lat, candidate.lon)} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>Check the pin on a map ↗</a>
          )}
          <div style={{ marginTop: '6px' }}>
            <button type="button" style={btnSmall} disabled={!candidate.name.trim() || Number.isNaN(parseFloat(candidate.lat)) || Number.isNaN(parseFloat(candidate.lon))}
                    onClick={() => {
                      setDraft((d) => ({ ...d, landmarks: [...(d.landmarks || []), {
                        name: candidate.name.trim(), lat: parseFloat(candidate.lat), lon: parseFloat(candidate.lon),
                        aliases: candidate.aliases.split(',').map((a) => a.trim()).filter(Boolean),
                      }] }));
                      setCandidate(null);
                      setLmText('');
                      setNote('Landmark added. Save to keep it.');
                    }}>
              I checked the pin: add landmark
            </button>
            <button type="button" style={btnSmall} onClick={() => setCandidate(null)}>Cancel</button>
          </div>
        </div>
      )}
      {(draft.landmarks || []).length > 0 && (
        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', marginBottom: '8px' }}>
          <thead><tr style={{ textAlign: 'left', color: BRAND.gray500 }}><th>Name</th><th>Aliases</th><th>Pin</th><th /></tr></thead>
          <tbody>
            {draft.landmarks.map((lm, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i} style={{ borderTop: `1px solid ${BRAND.rule}` }}>
                <td>{lm.name}</td>
                <td>{(lm.aliases || []).join(', ')}</td>
                <td><a href={mapLink(lm.lat, lm.lon)} target="_blank" rel="noreferrer">{lm.lat}, {lm.lon}</a></td>
                <td><button type="button" style={btnSmall} aria-label={`Remove ${lm.name}`}
                            onClick={() => setDraft((d) => ({ ...d, landmarks: d.landmarks.filter((_, j) => j !== i) }))}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <button type="button" style={btnSmall} disabled={!!busy}
              onClick={() => call('save', async () => { await save(draft); setNote('Landmarks saved.'); })}>
        Save landmarks
      </button>

      {/* ---- test trips ---- */}
      <h3 style={{ fontSize: '0.92rem', margin: '18px 0 4px' }}>Test trips</h3>
      <p style={hint}>A trip from each saved landmark to the busiest stop, next weekday at 10 AM. Flagged rows need a look; an airport with no stop nearby is expected, not a bug.</p>
      <button type="button" style={btnSmall} disabled={!!busy || !published}
              title={published ? '' : 'Publish the feed first'}
              onClick={() => call('smoke', async () => {
                setSmoke(await req('/api/corpus/transit/smoke', { method: 'POST', body: JSON.stringify({ corpus_id: corpusId }) }));
              })}>
        {busy === 'smoke' ? 'Planning trips…' : 'Run test trips'}
      </button>
      {smoke && (
        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', marginTop: '8px' }}>
          <caption style={{ textAlign: 'left', color: BRAND.gray500, marginBottom: '4px' }}>
            To {smoke.hub} · {smoke.when} · {smoke.flagged} flagged
          </caption>
          <thead><tr style={{ textAlign: 'left', color: BRAND.gray500 }}><th>From</th><th>Result</th><th>Routes</th><th>Leave → arrive</th></tr></thead>
          <tbody>
            {smoke.results.map((r) => (
              <tr key={r.landmark} style={{ borderTop: `1px solid ${BRAND.rule}`, background: r.flag ? '#fef3c7' : 'transparent' }}>
                <td>{r.landmark}</td>
                <td>
                  {r.status}{r.long_walk ? ' · long walk' : ''}
                  {r.nearest ? ` · nearest stop: ${r.nearest.stop} (${r.nearest.distance_mi} mi)` : ''}
                </td>
                <td>{(r.routes || []).join(', ')}</td>
                <td>{r.depart ? `${r.depart} → ${r.arrive}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ErrorList error={error} />
      {note && <p role="status" style={{ ...hint, color: '#065f46' }}>{note}</p>}
    </div>
  );
}
